import Card from '@mui/material/Card';
import { styled } from '@mui/material/styles';
import CardContent from '@mui/material/CardContent';
import _ from '@lodash';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import { sumBy } from 'lodash';
import { useEffect, memo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import NoItemMessage from 'app/main/apps/common/NoItemMessage';
import { Loader } from 'app/main/apps/common/ListInfoMessage';
import { displayName, formatDateString, formatter, getCustomerBasedProfile, getDiscountAmtFromPercentage, isCustomer ,  checkNullOrUndefined, isValidPositiveNumber} from 'app/main/apps/common/AuraFunctions';
import { getContactInfo, getInvoiceDetails } from '../../store/orderSlice';
import InvoiceDiscrepancyPanel from '../Dialogs/InvoiceDiscrepancyPanel';
import { discrepancyForDisplay } from '../Dialogs/invoiceDiscrepancyUtils';
import { isCreditNote, isDebitNote, noteBalanceEffect, noteTypeLabel, summariseAdjustmentNotes } from './invoiceAdjustmentNotes';
import { hasInvoice } from './invoiceTabState';

// "−$568.85" / "+$100.00": the sign says which way the balance moved.
const signedAmount = (value) => `${value < 0 ? '−' : '+'}${formatter.format(Math.abs(value))}`;

const Root = styled('div')(({ theme }) => ({
  '& table ': {
    '& th:first-of-type, & td:first-of-type': {
      paddingLeft: `${0}!important`,
    },
    '& th:last-child, & td:last-child': {
      paddingRight: `${0}!important`,
    },
  },

  '& .divider': {
    width: 1,
    backgroundColor: theme.palette.divider,
    height: 144,
  },

  '& .seller': {
    backgroundColor: theme.palette.primary.dark,
    color: theme.palette.getContrastText(theme.palette.primary.dark),
    marginRight: -88,
    paddingRight: 66,
    width: 480,
    '& .divider': {
      backgroundColor: theme.palette.getContrastText(theme.palette.primary.dark),
      opacity: 0.5,
    },
  },
}));

const InvoiceTab = (props) => {
  const isDialog = props.type === 'dialog'
  const dispatch = useDispatch()
  const [values, setValues] = useState({})
  const [invoiceDetails, setInvoiceDetails] = useState({})
  const [contactInfo, setContactInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showAmountDueSection, setShowAmountDueSection] = useState(null);
  const [discrepancy, setDiscrepancy] = useState(null);
  const [adjustmentNotes, setAdjustmentNotes] = useState([]);
  const currentUser = useSelector(({ user }) => user);

  useEffect(() => {
    if (!(props.id && props.id !== 'new')) {
      return undefined;
    }
    setLoading(true);
    const params = { [isDialog ? 'id' : 'orderId']: props.id };
    dispatch(getInvoiceDetails(params))
      .unwrap()
      .then((res) => {
        const invoiceRes = res?.invoice;
        // An order with no invoice yet comes back as {"invoice": {}}: show "no invoice", don't render one.
        if (!hasInvoice(invoiceRes)) {
          setValues({});
          setInvoiceDetails({});
          setDiscrepancy(null);
          setAdjustmentNotes([]);
          setLoading(false);
          return;
        }
        const groupedByOrderNo = invoiceRes.invoice_details?.reduce((acc, current) => {
          const key = current.order_no;
          if (!key) {
            return acc;
          }
          if (!acc[key]) {
            acc[key] = [];
          }
          acc[key].push(current);
          return acc;
        }, {});
        setInvoiceDetails(groupedByOrderNo);
        // PGSS-5306: prefer derived balance_due from notes when present
        const withBalance = {
          ...invoiceRes,
          due_amount: res.balance_due != null ? res.balance_due : invoiceRes.due_amount,
        };
        setValues(withBalance);
        setAdjustmentNotes(Array.isArray(res.adjustment_notes) ? res.adjustment_notes : []);
        setDiscrepancy(discrepancyForDisplay(res.discrepancy));
        setShowAmountDueSection(
          isValidPositiveNumber(invoiceRes.payments)
            || isValidPositiveNumber(invoiceRes.credits)
            || (Array.isArray(res.adjustment_notes) && res.adjustment_notes.length > 0)
        );
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });

    dispatch(getContactInfo()).then((action) => {
      if (action.payload) {
        setContactInfo(action.payload);
      }
    });
    return undefined;
  }, [props.id, isDialog, dispatch]);

  if (loading) return <Loader />

  const noteSummary = summariseAdjustmentNotes(adjustmentNotes);
  // Only relabel when notes exist, so invoices without notes keep showing BALANCE DUE as before.
  const isCreditBalance = adjustmentNotes.length > 0 && Number(values.due_amount) < 0;

  return (
    <Root className="grow shrink-0 p-0">
      {Object.keys(values).length ? (
        <Card className="w-xl mx-auto shadow-0">
          <CardContent className="p-88 print:p-0">

          {discrepancy && values?.id && !props.hideDiscrepancy && (
            <InvoiceDiscrepancyPanel
              invoiceId={values.id}
              discrepancy={discrepancy}
              readOnly
            />
          )}

          <div className="flex justify-between items-start pb-8">
              <div className="flex items-start">
                 <img className="w-[18rem] pr-16 object-contain" src={getCustomerBasedProfile.companyLogo} alt="logo" />
               <div className="border-l-[4px] pl-16 pr-20 flex flex-col justify-between h-full" style={{ borderColor: 'gray', borderStyle: 'solid' }}>
                    <Typography color="textSecondary">{contactInfo && contactInfo.name ? contactInfo.name : ''}</Typography>
                    <Typography color="textSecondary">{contactInfo && contactInfo.location? contactInfo.location : ''}</Typography>
                    <Typography color="textSecondary">{contactInfo && contactInfo.phone ? contactInfo.phone : ''}</Typography>
                    <Typography color="textSecondary">{values.bank_details?.['Company Email']}</Typography>
               </div>
            </div>

            <div className="text-right">
                 <div className="grid grid-cols-2 gap-x-20">
                    <Typography variant="body1" className="font-medium text-gray-600">INVOICE </Typography>
                    <Typography variant="body1" className="text-gray-900 text-left">{values.invoice_no}</Typography>

                    <Typography variant="body1" className="font-medium text-gray-600">INVOICE DATE</Typography>
                    <Typography variant="body1" className="text-gray-900 text-left">{values.invoice_date ? formatDateString(values.invoice_date) : ' '}</Typography>

                    <Typography variant="body1" className="font-medium text-gray-600">DUE DATE</Typography>
                    <Typography variant="body1" className="text-gray-900 text-left">{values.due_date ? formatDateString(values.due_date) : ' '}</Typography>

                    <Typography variant="body1" className="font-medium text-gray-600">TOTAL DUE</Typography>
                    <Typography variant="body1" className="text-gray-900 text-left">{ formatter.format(values.due_amount) }</Typography>

                   {!isCustomer(currentUser.role) && ( <Typography variant="body1" className="font-medium text-gray-600">TERMS</Typography>)}
                   {!isCustomer(currentUser.role) && ( <Typography variant="body1" className="text-gray-900 text-left">{values.customer?.invoice_term}</Typography>)}
                  
                  {!isCustomer(currentUser.role) && values.customer?.customer_payment_type && (
                    <>
                      <Typography variant="body1" className="font-medium text-gray-600">
                        PAYMENT TYPE
                      </Typography>
                      <Typography variant="body1" className="text-gray-900 text-left">
                        {values.customer.customer_payment_type}
                      </Typography>
                    </>
                  )}
              </div>
          </div>
      </div>

            <div className="flex mt-8">
                    <Typography variant="h6" className="ml-[6.8rem] text-gray-600 font-bold">CLIENT</Typography>
                <div className="pl-40">
                  <div className="border-l-[4px] pl-16 pr-20 flex flex-col justify-between h-full" style={{ borderColor: 'gray', borderStyle: 'solid' }}>

                    <Typography color="textSecondary" className="font-bold">{values.customer_name}</Typography>
                    {values.customer?.address_line_1 && <Typography color="textSecondary">{values.customer?.address_line_1}</Typography>}
                    {values.customer?.address_line_2 && <Typography color="textSecondary">{values.customer?.address_line_2}</Typography>}
                    {values.customer?.profile_name && <Typography color="textSecondary">{values.customer?.profile_name}</Typography>}
                    {values.customer?.phone && <Typography color="textSecondary">{values.customer?.phone}</Typography>}
                    {values.customer?.email && <Typography color="textSecondary">{values.customer?.email}</Typography>}
               </div>
           </div>
      </div>

            <div className="mt-64">
              <Table className="simple">
                <TableHead>
                  <TableRow>
                    <TableCell>No</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>REFERENCE NO</TableCell>
                    <TableCell>DECEDENT</TableCell>
                    <TableCell align="right">PRICE</TableCell>
                    <TableCell align="right">QTY</TableCell>
                    <TableCell align="right">DISCOUNT</TableCell>
                    <TableCell align="right">TOTAL</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {
                    Object.keys(invoiceDetails).map((key, index) => {
                      return (
                        <>
                        <TableRow>
                           <TableCell colSpan={8} className='!border-none' >
                            <Typography className={`mt-10 font-medium  text-lg ${index > 0 && 'mt-52'}`}>{`Order #${key}`}</Typography>
                           </TableCell>
                        </TableRow>
                          {invoiceDetails[key]?.map((orderDetail, orderDetailIndex) => {
                            if (orderDetail?.name === 'Trade Discount') return <></>
                            return (
                              /* type = 3  Shipping cost,  type = 4 Tax  */
                              orderDetail.type !== 3 && <TableRow key={orderDetail.invoice_detail_id}>
                                <TableCell>
                                  <Typography>{orderDetailIndex + 1}</Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="subtitle1">{orderDetail.name}</Typography>
                                </TableCell>
                                <TableCell>
                                  {orderDetail.customer_provider_number}
                                </TableCell>
                                <TableCell>
                                  {orderDetail.decedents}
                                </TableCell>
                                <TableCell align="right">
                                  {(orderDetail.type  && orderDetail.type !== 1) ? '-' : formatter.format(orderDetail.unit_price)}
                                </TableCell>
                                <TableCell align="right">{(orderDetail.type  && orderDetail.type !== 1) ? '-' : orderDetail.quantity}</TableCell>
                                
                                {/* This portion does not display discount value for Surcharge - line item 2 */}
                                {orderDetail.type !== 2 ? (
                                  <TableCell align="right">
                                    ${getDiscountAmtFromPercentage(
                                      orderDetail?.discount, 
                                      orderDetail?.unit_price, 
                                      orderDetail?.quantity
                                    )}
                                  </TableCell>
                                ) : (<TableCell align="right">-</TableCell>)}

                                <TableCell align="right">
                                  {orderDetail.amount !== null ? formatter.format(orderDetail.amount) : '-'}
                                </TableCell>
                              </TableRow>
                            )
                          })}
                          <TableRow>
                            <TableCell colSpan={6} >
                              <Typography className="font-normal" variant="subtitle1" color="textSecondary">
                                ORDER SUBTOTAL
                              </Typography>
                            </TableCell>
                            <TableCell  colSpan={2} align="right">
                              <Typography className="font-normal" variant="subtitle1" color="textSecondary">
                                {formatter.format(sumBy(invoiceDetails[key], (d) => {
                                  if (d.type !== 3) return d.amount
                                  return 0
                                }))}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        </>
                      )
                    })}
                </TableBody >
              </Table>

              <Table className="simple mt-32">
                <TableBody>
                  <TableRow>
                    <TableCell>
                      <Typography className="font-normal" variant="subtitle1" color="textSecondary">
                        SUBTOTAL
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography className="font-normal" variant="subtitle1" color="textSecondary">
                        {formatter.format(sumBy(values.invoice_details, (d) => {
                          if (d.type !== 3) return d.amount
                          return 0
                        }))}
                      </Typography>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <Typography className="font-normal" variant="subtitle1" color="textSecondary">
                        Shipping Cost
                      </Typography>
                      <Typography className="font-normal text-13" variant="subtitle1" color="textPrimary">
                        {values.comments_to_send ? `(${values.comments_to_send})` : null}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography className="font-normal" variant="subtitle1" color="textSecondary">
                        {formatter.format(values.invoice_details?.find(d => d.type === 3)?.amount || 0)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                  {
                    values.invoice_details?.find(d => d.type === 4)?.name && (
                      <TableRow>
                        <TableCell>
                          <Typography className="font-normal" variant="subtitle1" color="textSecondary">
                            {values.invoice_details?.find(d => d.type === 4)?.name}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography className="font-normal" variant="subtitle1" color="textSecondary">
                            {formatter.format(values.invoice_details?.find(d => d.type === 4)?.amount ? -Math.abs(values.invoice_details.find(d => d.type === 4).amount) : 0)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )
                  }
                  <TableRow>
                    <TableCell>
                      <Typography className="font-normal" variant="subtitle1" color="textSecondary">
                        {displayName?.tax || "TAX"}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography className="font-normal" variant="subtitle1" color="textSecondary">
                        {/* {formatter.format(
                          values.commission_rate ? values.commission_rate : 0
                        )} */}
                         {formatter.format(
                          values.tax ? values.tax : 0
                        )}
                      </Typography>
                    </TableCell>
                  </TableRow>
                  {/* <TableRow>
                    <TableCell>
                      <Typography className="font-normal" variant="subtitle1" color="textSecondary">
                        DISCOUNT
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography className="font-normal" variant="subtitle1" color="textSecondary">
                        {formatter.format(props.order.discount)}
                      </Typography>
                    </TableCell>
                  </TableRow> */}
                  <TableRow>
                    <TableCell sx={{borderBottom: '0px !important'}}>
                      <Typography className="font-light" variant="h4" color="textSecondary">
                        {showAmountDueSection ? "INVOICE TOTAL" : "TOTAL"}
                      </Typography>
                    </TableCell>
                    <TableCell align="right"  sx={{borderBottom: '0px !important'}}>
                      <Typography className="font-light" variant="h4" color="textSecondary">
                        {formatter.format(values.invoice_amount)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                  {showAmountDueSection ? (
                    <>
                      {values?.payments > 0 &&
                        <TableRow>
                            <TableCell
                              sx={{borderBottom: isValidPositiveNumber(values.credits) ? "0px !important": undefined}}
                            >
                              <Typography className="font-light mt-20" variant="h5" color="textSecondary" >
                                AMOUNT PAID
                              </Typography>
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{borderBottom: isValidPositiveNumber(values.credits) ? "0px !important": undefined}}
                            >
                              <Typography className="font-light mt-20" variant="h5" color="textSecondary" >
                                {formatter.format(values.payments)}
                              </Typography>
                            </TableCell>
                        </TableRow>
                      }
                      {values?.credits > 0 &&
                        <TableRow>
                          <TableCell>
                            <Typography className="font-light mb-5" variant="h5" color="textSecondary" >
                              TOTAL CREDITS
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography className="font-light mb-5" variant="h5" color="textSecondary" >
                              {formatter.format(values.credits)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      }
                      {noteSummary.creditTotal > 0 &&
                        <TableRow>
                          <TableCell>
                            <Typography className="font-light" variant="h5" color="textSecondary" >
                              LESS CREDIT NOTES
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {noteSummary.creditNumbers.join(', ')}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography className="font-light" variant="h5" color="textSecondary" >
                              {signedAmount(-noteSummary.creditTotal)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      }
                      {noteSummary.debitTotal > 0 &&
                        <TableRow>
                          <TableCell>
                            <Typography className="font-light" variant="h5" color="textSecondary" >
                              ADD DEBIT NOTES
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {noteSummary.debitNumbers.join(', ')}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography className="font-light" variant="h5" color="textSecondary" >
                              {signedAmount(noteSummary.debitTotal)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      }
                      <TableRow>
                        <TableCell>
                          <Typography className="font-medium" variant="h5" color="textSecondary" >
                            {/* The backend returns a negative balance when notes exceed what is owed. */}
                            {isCreditBalance ? 'CREDIT BALANCE' : 'BALANCE DUE'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography className="font-medium" variant="h5" color="textSecondary" >
                            {formatter.format(isCreditBalance ? Math.abs(values.due_amount) : values.due_amount)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    </>
                  ) : (
                    <></>
                  )}
                </TableBody>
              </Table>
            </div>

            <div className={`${showAmountDueSection ? "mt-64" : "mt-96"}`}>
              {/* <Typography className="mb-8" variant="h5" color="textSecondary">
                Payment Information
              </Typography>
              <Typography color="textSecondary">Via ACH / Wire Transfer</Typography>
              <div>{values.bank_details?.['Bank Name'] || ''}</div>
              <div>{values.bank_details?.['Bank Street'] || ''}</div>
              <div>
                {values.bank_details?.['Bank City'] || ''}, {values.bank_details?.['Bank State'] || ''}, {values.bank_details?.['Bank Zipcode'] || ''}
              </div>
              <div>
                <span>Swift Code: </span>
                <span>{values.bank_details?.['Bank Swift Code'] || ''}</span>
              </div>
              <div>
                <span>Account: </span>
                <span>{values.bank_details?.['Bank Account'] || ''}</span>
              </div>
              <div>
                <span>Routing: </span>
                <span>{values.bank_details?.['Bank Routing'] || ''}</span>
              </div> */}

              {adjustmentNotes.length > 0 && (
                <div className="print:hidden rounded-md border p-16">
                  <Typography
                    variant="h6"
                    className="font-semibold mb-12 px-12 py-8 rounded-md border-l-4 border-blue-600 bg-blue-50 text-blue-900"
                  >
                    Credit / debit notes on this invoice
                  </Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Note no.</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Issued</TableCell>
                        <TableCell>Reason</TableCell>
                        <TableCell align="right">Effect on balance</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {adjustmentNotes.map((n) => {
                        const effect = noteBalanceEffect(n);
                        const credit = isCreditNote(n);
                        const debit = isDebitNote(n);
                        // An unrecognised type is not counted in the totals either, so it gets a
                        // neutral chip and no effect rather than being shown as a debit.
                        let chipColor = 'default';
                        if (credit) chipColor = 'success';
                        else if (debit) chipColor = 'warning';
                        let effectClass;
                        if (credit) effectClass = 'text-green-700';
                        else if (debit) effectClass = 'text-orange-800';
                        return (
                          <TableRow key={n.id}>
                            <TableCell>{n.note_number}</TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                variant="outlined"
                                color={chipColor}
                                label={noteTypeLabel(n)}
                              />
                            </TableCell>
                            <TableCell>{n.issued_at ? formatDateString(n.issued_at) : '—'}</TableCell>
                            <TableCell>{n.reason || '—'}</TableCell>
                            <TableCell align="right" className={effectClass}>
                              {credit || debit ? signedAmount(effect) : '—'}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      {adjustmentNotes.length > 1 && (
                        <TableRow>
                          <TableCell colSpan={4} className="font-medium">Net adjustment</TableCell>
                          <TableCell align="right" className="font-medium">{signedAmount(noteSummary.net)}</TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              )}

              <Typography className='mt-12' color="textSecondary">{values.bank_details?.['Check Payable Text'] || ''}</Typography>
              <span>{values.bank_details?.['Check Payable To'] || ''}</span>
              { ((values.customer?.invoice_term && (values.customer?.invoice_term === 0 || values.customer?.invoice_term === 'Due on receipt')) || checkNullOrUndefined(values.customer?.invoice_term)) ?
                <Typography className={`${showAmountDueSection ? "mt-12" : "mt-24"} mb-24  print:mb-12`}  variant="body1">
                  Please pay immediately. Thank you for your business.
                </Typography>
                :
                <Typography className={`${showAmountDueSection ? "mt-12" : "mt-24"} mb-24  print:mb-12`}  variant="body1">
                  Please pay within {values.pay_term} days. Thank you for your business.
                </Typography>
              }
            </div>
          </CardContent>
        </Card>
      ) :
        <NoItemMessage message="Invoice yet to be generated" />
      }
    </Root>
  );
};

export default memo(InvoiceTab);
